import * as XLSX from "xlsx";
import moment from "moment";
import {levenshtein, readWorkbookFromFile} from "./athleticTestImport";

export {readWorkbookFromFile};

/**
 * Import "Report Gara NUMERICO" portieri da CSV/XLSX.
 *
 * Layout reale (indici riga 0-based, template U.C. Sampdoria):
 *  - metadata: etichetta e valore nella STESSA cella (es. "PARTITA: SAMP - MONACO",
 *    "GIOCATORE: DAVIDE FERRARI", "DATA: 14-08-2026"); il valore è ciò che segue il primo ":".
 *  - offensivo/difensivo: POS in colonna 2, NEG in colonna 3.
 *  - blocco angoli (label in col.4): valore in colonna 9.
 *  - marcatura UOMO/ZONA MISTA/ZONA: colonne 5/7/9 sulla riga 15, indicata con una "X".
 */

// Metadata: [riga, colonna] della cella "ETICHETTA: valore".
const META = {
	match: [1, 0],
	reasonReplaced: [1, 4],
	reportDate: [2, 0],
	reasonWarned: [2, 4],
	playerName: [3, 0],
	reasonExpelled: [3, 4],
	minutesPlayed: [4, 0],
	notes: [4, 4],
	heatingExecution: [5, 0],
	generalOpinion: [6, 0],
	overallRating: [7, 0], // VALUTAZIONE 1-10 (oggi fuori dal modello)
};

const GOAL_ROWS = [9, 10, 11, 12, 13]; // colonna 0, formato "N: descrizione"

// offensivo/difensivo: {base}Ok = col.2 (POS), {base}Failed = col.3 (NEG)
const OFFENSIVE = [
	[15, "shortPass"],
	[16, "longMidThrow"],
	[17, "restartWithHands"],
	[18, "remittalOnTheFly"],
	[19, "positiveTransitionOutside"],
];
const DEFENSIVE = [
	[22, "saveGrip"],
	[23, "saveRebound"],
	[24, "highClaim"],
	[25, "highPunch"],
	[26, "lowDive"],
	[27, "depthCoverageInside"],
	[28, "depthCoverageOutside"],
];

// angoli: valore in colonna 9 (le righe "subtotale/totale" 16, 22, 30 sono derivate e si ignorano)
const CORNER_VALUE_COL = 9;
const CORNERS = [
	[17, "cornerOutIntervention"],
	[18, "cornerOutSave"],
	[19, "cornerOutDefendedTeammate"],
	[20, "cornerOutOpponents"],
	[21, "cornerOutGoal"],
	[23, "cornerRetIntervention"],
	[24, "cornerRetSave"],
	[25, "cornerRetDefendedTeammate"],
	[26, "cornerRetOpponents"],
	[27, "cornerRetGoal"],
	[28, "blockedCorners"],
	[29, "shortCorners"],
];

// marcatura (riga 15): "X" indica il tipo usato
const MARKING = {
	manToManMarking: [15, 5],
	mixedMarking: [15, 7],
	zonalMarking: [15, 9],
};
const TOTAL_ATTACKS_CELL = [31, 9]; // NUMERO TOTALE ATTACCHI AREA

const cell = (rows, r, c) => (rows[r] ? rows[r][c] : null);

const toTxt = (v) => (v === null || v === undefined ? "" : String(v).trim());
const toNum = (v) => {
	if (v === null || v === undefined || v === "") return 0;
	const n = parseFloat(String(v).replace(",", "."));
	return Number.isNaN(n) ? 0 : n;
};

// valore che segue il primo ":" (etichetta e valore stanno nella stessa cella)
const stripValue = (raw) => {
	const s = String(raw ?? "");
	const i = s.indexOf(":");
	return (i === -1 ? s : s.slice(i + 1)).trim();
};

// marcatura: la "X" è una checkbox -> 1 (selezionata); numero -> numero; vuoto -> 0
const markingValue = (raw) => {
	const s = String(raw ?? "").trim();
	if (!s) return 0;
	const n = parseFloat(s.replace(",", "."));
	return Number.isNaN(n) ? 1 : n;
};

/**
 * Legge il workbook e ritorna il report parsato (un portiere, una partita).
 * @returns {{fields: object, playerName: string, matchLabel: string, reportDate: string|null, overallRating: number, warnings: string[]}}
 */
export const parseMatchReportWorkbook = (workbook) => {
	const warnings = [];
	const ws = workbook.Sheets[workbook.SheetNames[0]];
	const rows = XLSX.utils.sheet_to_json(ws, {
		header: 1,
		defval: null,
		blankrows: true,
		raw: false,
	});

	const title = toTxt(cell(rows, 0, 0)).toUpperCase();
	if (!title.includes("REPORT GARA") && !title.includes("REPORT NUMERICO")) {
		warnings.push(
			'Intestazione non riconosciuta ("Report Gara NUMERICO"): verifica il file.',
		);
	}

	// metadata (etichetta+valore nella stessa cella)
	const meta = {};
	Object.entries(META).forEach(([k, [r, c]]) => {
		meta[k] = stripValue(cell(rows, r, c));
	});

	const fields = {};
	fields.minutesPlayed =
		parseInt(String(meta.minutesPlayed).replace(/[^0-9]/g, ""), 10) || 0;
	fields.heatingExecution = meta.heatingExecution;
	fields.reasonReplaced = meta.reasonReplaced;
	fields.reasonWarned = meta.reasonWarned;
	fields.reasonExpelled = meta.reasonExpelled;
	fields.notes = meta.notes;
	fields.generalOpinion = meta.generalOpinion;

	// goal subiti
	const goals = GOAL_ROWS.map((r) => stripValue(cell(rows, r, 0))).filter(
		Boolean,
	);
	goals.forEach((g, i) => {
		fields["descriptionGoalConceded" + i] = g;
	});
	fields.totalGoalsConceded = goals.length;

	// offensivo / difensivo (Ok = POS col.2, Failed = NEG col.3)
	OFFENSIVE.concat(DEFENSIVE).forEach(([r, base]) => {
		fields[base + "Ok"] = toNum(cell(rows, r, 2));
		fields[base + "Failed"] = toNum(cell(rows, r, 3));
	});

	// angoli (valore col.9)
	CORNERS.forEach(([r, key]) => {
		fields[key] = toNum(cell(rows, r, CORNER_VALUE_COL));
	});

	// marcatura (X = checkbox selezionata -> 1)
	fields.manToManMarking = markingValue(cell(rows, ...MARKING.manToManMarking));
	fields.mixedMarking = markingValue(cell(rows, ...MARKING.mixedMarking));
	fields.zonalMarking = markingValue(cell(rows, ...MARKING.zonalMarking));

	fields.totalAttacksArea = toNum(cell(rows, ...TOTAL_ATTACKS_CELL));

	// data -> ISO
	const parsedDate = moment(
		meta.reportDate,
		["DD-MM-YYYY", "D-M-YYYY", "DD/MM/YYYY", "YYYY-MM-DD"],
		true,
	);

	return {
		fields,
		playerName: meta.playerName,
		matchLabel: meta.match,
		reportDate: parsedDate.isValid() ? parsedDate.format("YYYY-MM-DD") : null,
		overallRating: toNum(meta.overallRating),
		warnings,
	};
};

const normalize = (v) =>
	String(v ?? "")
		.normalize("NFD")
		.replace(/[̀-ͯ]/g, "")
		.toUpperCase()
		.replace(/\s+/g, " ")
		.trim();

/**
 * Match del portiere per nome (cognome, "cognome nome" o "nome cognome").
 * @returns {{player: object|null, confidence: 'exact'|'probable'|'none', candidates: object[]}}
 */
export const matchPlayerByName = (rawName, players) => {
	const target = normalize(rawName);
	if (!target)
		return {player: null, confidence: "none", candidates: players || []};

	const withMeta = (players || []).map((p) => {
		const last = normalize(p.lastName);
		const full1 = normalize(`${p.lastName} ${p.firstName || ""}`);
		const full2 = normalize(`${p.firstName || ""} ${p.lastName}`);
		const distance = Math.min(
			levenshtein(target, last),
			levenshtein(target, full1),
			levenshtein(target, full2),
		);
		return {player: p, last, full1, full2, distance};
	});

	const exact = withMeta.find(
		(m) =>
			m.last === target ||
			m.full1 === target ||
			m.full2 === target ||
			m.distance === 0,
	);
	if (exact) return {player: exact.player, confidence: "exact", candidates: []};

	const fuzzy = withMeta
		.filter((m) => m.distance <= 2)
		.sort((a, b) => a.distance - b.distance);
	if (fuzzy.length)
		return {player: fuzzy[0].player, confidence: "probable", candidates: []};

	const candidates = withMeta
		.sort((a, b) => a.distance - b.distance)
		.slice(0, 5)
		.map((m) => m.player);
	return {player: null, confidence: "none", candidates};
};
